import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/tokens.css';

const LoginPage               = React.lazy(() => import('./pages/auth/LoginPage'));
const SuspendedPage           = React.lazy(() => import('./pages/auth/SuspendedPage'));
const ForceChangePasswordPage = React.lazy(() => import('./pages/auth/ForceChangePasswordPage'));
const SALayout       = React.lazy(() => import('./pages/sa/SALayout'));
const SADashboard    = React.lazy(() => import('./pages/sa/SADashboard'));
const SATenantsPage  = React.lazy(() => import('./pages/sa/SATenantsPage'));
const SABillingPage  = React.lazy(() => import('./pages/sa/SABillingPage'));
const SAAuditPage    = React.lazy(() => import('./pages/sa/SAAuditPage'));
const SASettingsPage = React.lazy(() => import('./pages/sa/SASettingsPage'));
const SAProfilePage  = React.lazy(() => import('./pages/sa/SAProfilePage'));
const TenantLayout   = React.lazy(() => import('./pages/tenant/TenantLayout'));
const Dashboard      = React.lazy(() => import('./pages/tenant/Dashboard'));
const ClientsPage    = React.lazy(() => import('./pages/tenant/clients/ClientsPage'));
const ClientDetail   = React.lazy(() => import('./pages/tenant/clients/ClientDetail'));
const ClientForm     = React.lazy(() => import('./pages/tenant/clients/ClientForm'));
const ProjectsPage   = React.lazy(() => import('./pages/tenant/projects/ProjectsPage'));
const ProjectDetail  = React.lazy(() => import('./pages/tenant/projects/ProjectDetail'));
const ProjectForm    = React.lazy(() => import('./pages/tenant/projects/ProjectForm'));
const FinancePage    = React.lazy(() => import('./pages/tenant/finance/FinancePage'));
const RecordPayment  = React.lazy(() => import('./pages/tenant/finance/RecordPayment'));
const ReceiptView    = React.lazy(() => import('./pages/tenant/finance/ReceiptView'));
const UsersPage      = React.lazy(() => import('./pages/tenant/users/UsersPage'));
const SettingsPage   = React.lazy(() => import('./pages/tenant/settings/SettingsPage'));
const SMSPage        = React.lazy(() => import('./pages/tenant/sms/SMSPage'));
const ReportsPage    = React.lazy(() => import('./pages/tenant/reports/ReportsPage'));

const Spinner = () => (
  <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh' }}>
    <div style={{ width:32,height:32,border:'3px solid #E8F0E0',borderTopColor:'#1A2E0F',borderRadius:'50%',animation:'spin 0.7s linear infinite' }}/>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const RequireAuth = ({ children }) => {
  const { user, loading, mustChangePassword } = useAuth();
  if (loading) return <Spinner/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (mustChangePassword) return <Navigate to="/set-password" replace/>;
  return children;
};

const RequireSA = ({ children }) => {
  const { user, isSA, loading, mustChangePassword } = useAuth();
  if (loading) return <Spinner/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (mustChangePassword) return <Navigate to="/set-password" replace/>;
  if (!isSA) return <Navigate to="/dashboard" replace/>;
  return children;
};

const RootRedirect = () => {
  const { user, isSA, loading, mustChangePassword } = useAuth();
  if (loading) return <Spinner/>;
  if (!user) return <Navigate to="/login" replace/>;
  if (mustChangePassword) return <Navigate to="/set-password" replace/>;
  if (isSA) return <Navigate to="/sa/dashboard" replace/>;
  return <Navigate to="/dashboard" replace/>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <React.Suspense fallback={<Spinner/>}>
          <Routes>
            <Route path="/"             element={<RootRedirect/>}/>
            <Route path="/login"        element={<LoginPage/>}/>
            <Route path="/suspended"    element={<SuspendedPage/>}/>
            <Route path="/set-password" element={<ForceChangePasswordPage/>}/>

            {/* Super Admin */}
            <Route path="/sa" element={<RequireSA><SALayout/></RequireSA>}>
              <Route index element={<Navigate to="dashboard" replace/>}/>
              <Route path="dashboard" element={<SADashboard/>}/>
              <Route path="tenants"   element={<SATenantsPage/>}/>
              <Route path="billing"   element={<SABillingPage/>}/>
              <Route path="audit"     element={<SAAuditPage/>}/>
              <Route path="settings"  element={<SASettingsPage/>}/>
              <Route path="profile"   element={<SAProfilePage/>}/>
            </Route>

            {/* Tenant app — all modules complete */}
            <Route path="/" element={<RequireAuth><TenantLayout/></RequireAuth>}>
              <Route path="dashboard"              element={<Dashboard/>}/>
              <Route path="clients"                element={<ClientsPage/>}/>
              <Route path="clients/new"            element={<ClientForm/>}/>
              <Route path="clients/:id"            element={<ClientDetail/>}/>
              <Route path="clients/:id/edit"       element={<ClientForm/>}/>
              <Route path="projects"               element={<ProjectsPage/>}/>
              <Route path="projects/new"           element={<ProjectForm/>}/>
              <Route path="projects/:id"           element={<ProjectDetail/>}/>
              <Route path="projects/:id/edit"      element={<ProjectForm/>}/>
              <Route path="finance"                element={<FinancePage/>}/>
              <Route path="finance/payment/new"    element={<RecordPayment/>}/>
              <Route path="finance/receipts/:id"   element={<ReceiptView/>}/>
              <Route path="users"                  element={<UsersPage/>}/>
              <Route path="settings"               element={<SettingsPage/>}/>
              <Route path="sms"                    element={<SMSPage/>}/>
              <Route path="reports"                element={<ReportsPage/>}/>
            </Route>

            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </React.Suspense>

        <Toaster
          position="top-right"
          toastOptions={{
            style:{ fontFamily:'DM Sans, sans-serif', fontSize:14 },
            success:{ iconTheme:{ primary:'#2D5016', secondary:'#E8F0E0' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
